import React, { useMemo } from 'react';
import { Card, Label } from 'semantic-ui-react';
import { FeedPost, isVideoMedia, formatDuration } from '../../../models/feed';
import { useHistory } from 'react-router-dom';
import { getContrastTextColor } from '../../../utils/getContrastTextColor';
import styles from './FeedCard.module.scss';
import moment from 'moment';
import elginLogo from '../../../static/img/elgin-logo-white.png';

interface FeedCardProps {
    post: FeedPost;
}

export const FeedCard: React.FC<FeedCardProps> = ({ post }) => {
    const history = useHistory();

    const handleClick = () => {
        history.push(`/${post.type === 'news' ? 'news' : 'events'}/${post.id}`);
    };

    // Detect video and multi-media
    const firstMediaItem = post.images?.[0];
    const hasVideo = useMemo(
        () => firstMediaItem && isVideoMedia(firstMediaItem.mediaType),
        [firstMediaItem]
    );
    const hasMultipleMedia = post.images && post.images.length > 1;

    // Get display image URL (thumbnail for video, regular image otherwise)
    const displayImageUrl = useMemo(() => {
        if (hasVideo && firstMediaItem?.thumbnailUrl) {
            return firstMediaItem.thumbnailUrl;
        }
        return post.imageUrl;
    }, [hasVideo, firstMediaItem, post.imageUrl]);

    const videoDuration = hasVideo ? firstMediaItem?.duration : null;

    const getNextEventDate = () => {
        if (post.type !== 'event' || !post.eventDates || post.eventDates.length === 0) {
            return null;
        }

        const sortedDates = [...post.eventDates].sort((a, b) =>
            new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime()
        );

        return sortedDates[0];
    };

    const nextEventDate = getNextEventDate();

    return (
        <Card
            className={styles.feedCard}
            onClick={handleClick}
            link
        >
            {displayImageUrl && (
                <div
                    className={styles.cardImageContainer}
                    style={{ backgroundImage: `url(${displayImageUrl})` }}
                >
                    <img
                        src={displayImageUrl}
                        alt={post.title}
                        className={styles.cardImage}
                        onError={(e: any) => {
                            // On error, hide container and show placeholder
                            e.currentTarget.parentElement!.style.display = 'none';
                        }}
                    />
                    {/* Video play icon overlay */}
                    {hasVideo && (
                        <div className={styles.videoOverlay}>
                            <div className={styles.playButton}>
                                <i className="play icon" />
                            </div>
                            {videoDuration && videoDuration > 0 && (
                                <div className={styles.durationBadge}>
                                    {formatDuration(videoDuration)}
                                </div>
                            )}
                        </div>
                    )}
                    {/* Multi-media dots indicator */}
                    {hasMultipleMedia && (
                        <div className={styles.dotsIndicator}>
                            {post.images.slice(0, 5).map((_, index) => (
                                <div
                                    key={index}
                                    className={`${styles.dot} ${
                                        index === 0 ? styles.dotActive : ''
                                    }`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
            <div
                className={styles.placeholderImage}
                style={{ display: displayImageUrl ? 'none' : 'flex' }}
            >
                <img
                    src={elginLogo}
                    alt="City of Elgin"
                    className={styles.placeholderLogo}
                />
            </div>
            <Card.Content>
                <div className={styles.badges}>
                    {post.category && (
                        <Label
                            size="small"
                            style={{
                                backgroundColor: post.category.color || '#005daa',
                                color: getContrastTextColor(post.category.color || '#005daa'),
                            }}
                            className={styles.categoryBadge}
                        >
                            {post.category.displayName}
                        </Label>
                    )}
                    {post.isUrgent && (
                        <Label color="red" size="small">
                            Urgent
                        </Label>
                    )}
                </div>
                <Card.Header className={styles.cardTitle}>
                    {post.title}
                </Card.Header>
                <Card.Meta className={styles.cardMeta}>
                    {post.type === 'news' && (
                        <span>{moment(post.publishedAt).format('MMMM D, YYYY')}</span>
                    )}
                    {post.type === 'event' && nextEventDate && (
                        <span>
                            <i className="calendar icon"></i>
                            {moment(nextEventDate.eventDate).format('MMMM D, YYYY')}
                            {nextEventDate.startTime && ` • ${nextEventDate.startTime}`}
                        </span>
                    )}
                </Card.Meta>
                {post.type === 'event' && post.eventLocation && (
                    <div className={styles.location}>
                        <i className="map marker icon"></i>
                        {post.eventLocation}
                    </div>
                )}
                {post.summary && (
                    <Card.Description className={styles.cardDescription}>
                        {post.summary}
                    </Card.Description>
                )}
            </Card.Content>
        </Card>
    );
};
